/**
 * ONEpop 화면 전용 아이콘. wallet-info/components/icons.tsx 와 동일한 규약
 * (`size` prop + `currentColor` stroke)을 따른다.
 *
 * 브랜드 아트워크(로고 워드마크 / 히어로 일러스트)는 Figma 원본이 래스터라
 * 여기서는 인라인 SVG 재현본을 둔다. 실제 에셋이 CDN에 올라가면
 * `OnePopBody`의 `logoSrc` / `heroSrc` prop 으로 덮어쓸 수 있다.
 */

interface IconProps {
  size?: number;
  className?: string;
}

/**
 * 액션 행 / 진입 버튼용 하트. Figma 원본(`heart.svg`)이며 하트 안에 숫자 1이
 * 들어간 ONE 브랜드 마크다 — 이전엔 lucide/heart 를 그대로 썼다.
 *
 * 원본에서 한 가지만 조정했다. `stroke="white" strokeOpacity="0.65"` →
 * `currentColor`. Figma 는 다크 배경 기준으로 내보내서 흰색이 박혀 있는데,
 * 하드코딩하면 라이트 테마에서 사라진다. 색은 `.wi-action-card` 가
 * `--ds-content-high` 로 준다. 0.65 도 같이 뺐다 — 같은 행의 Receive/Send
 * 아이콘은 불투명이라 그것만 흐려 보인다.
 */
export function OnePopActionIcon({ size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M1.66675 7.91662C1.66677 6.98929 1.94808 6.08377 2.47353 5.31967C2.99898 4.55557 3.74385 3.96883 4.60976 3.63695C5.47567 3.30507 6.42188 3.24366 7.32343 3.46082C8.22497 3.67799 9.03944 4.16352 9.65925 4.85329C9.7029 4.89996 9.75568 4.93718 9.81431 4.96262C9.87294 4.98806 9.93617 5.00119 10.0001 5.00119C10.064 5.00119 10.1272 4.98806 10.1859 4.96262C10.2445 4.93718 10.2973 4.89996 10.3409 4.85329C10.9588 4.15904 11.7734 3.66943 12.6764 3.44962C13.5795 3.22982 14.528 3.29024 15.3958 3.62286C16.2636 3.95547 17.0096 4.5445 17.5343 5.31154C18.0591 6.07858 18.3378 6.98725 18.3334 7.91662C18.3334 9.82495 17.0834 11.25 15.8334 12.5L11.2567 16.9275C11.1015 17.1058 10.91 17.249 10.6951 17.3477C10.4802 17.4464 10.2468 17.4982 10.0103 17.4997C9.77386 17.5012 9.53979 17.4523 9.32365 17.3564C9.10752 17.2605 8.91427 17.1196 8.75675 16.9433L4.16675 12.5C2.91675 11.25 1.66675 9.83329 1.66675 7.91662Z"
        stroke="currentColor"
        strokeWidth={1.25}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.84839 8.28833H10V13.4938"
        stroke="currentColor"
        strokeWidth={1.25}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Send POP (lucide/send). */
export function OnePopSendIcon({ size = 24, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m21.854 2.147-10.94 10.939"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Claim POPs (lucide/hand-coins). */
export function OnePopClaimIcon({ size = 24, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17" />
        <path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
        <path d="m2 16 6 6" />
        <circle cx="16" cy="9" r="2.9" />
        <circle cx="6" cy="5" r="3" />
      </g>
    </svg>
  );
}

/** Activity (lucide/scroll-text). */
export function OnePopActivityIcon({ size = 24, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M15 12h-5M15 8h-5" />
        <path d="M19 17V5a2 2 0 0 0-2-2H4" />
        <path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3" />
      </g>
    </svg>
  );
}

/** "Get ONEUSD ›" / "See all activity ›" 의 꺾쇠. */
export function OnePopChevronRightIcon({ size = 12, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="m9 18 6-6-6-6"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** 히어로 문구 안의 X(구 트위터) 배지. */
export function XBadgeIcon({ size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <rect width="20" height="20" rx="4.8" fill="currentColor" />
      <path
        d="M11.53 9.05 15.7 4.4h-.99l-3.62 4.04L8.2 4.4H4.86l4.37 6.1-4.37 4.87h.99l3.82-4.26 3.05 4.26h3.34zm-1.35 1.51-.44-.61-3.52-4.9h1.52l2.84 3.96.44.61 3.7 5.14h-1.52z"
        fill="var(--wi-op-x-glyph, #ffffff)"
      />
    </svg>
  );
}

/** 빈 상태 항목 1 — "No wallet address needed". */
export function OnePopHandleIcon({ size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="9" cy="7" r="3.4" />
        <path d="M2.6 20.4a6.4 6.4 0 0 1 10.2-5.15" />
        <circle cx="17.5" cy="17.5" r="2.1" />
        <path d="M19.6 17.5v1.05a1.55 1.55 0 0 0 3.1 0v-1.05a5.2 5.2 0 1 0-2.05 4.14" />
      </g>
    </svg>
  );
}

/** 빈 상태 항목 2 — "Claim instantly". */
export function OnePopInstantIcon({ size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12.5 3 5 13.5h5.5L10 21l7.5-10.5H12z" />
        <path d="M20 4.5 21.5 3M20.5 8h2M17 2.5V1" />
      </g>
    </svg>
  );
}

/** 빈 상태 항목 3 — "Refund anytime". */
export function OnePopRefundIcon({ size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
        <path d="M12 9v6" />
        <path d="m9.5 12.5 2.5 2.5 2.5-2.5" />
      </g>
    </svg>
  );
}

/**
 * ONEpop 워드마크 — 하트(숫자 1) 마크 + "ONEpop" 레터폼. Figma 브랜드 에셋
 * 원본이며, 재현본이 아니므로 임의로 좌표를 손대지 않는다. 실제 이미지 파일로
 * 바꾸려면 `OnePopBody` 의 `logoSrc` 를 쓴다.
 *
 * 원본에서 두 가지만 조정했다.
 * - 레터폼 `fill` 을 `black` → `currentColor`. 하드코딩하면 다크 테마에서
 *   배경과 붙는다. 색은 `.wi-op-logo` 가 `--ds-content-highest` 로 준다.
 * - gradient/mask id 에 `wi-op-logo-` 접두사. Figma 내보내기의
 *   `paint0_linear_6126_2359` 같은 id 는 문서 전역이라 다른 내보내기와 충돌할
 *   수 있다. 마스크는 alpha 타입이고 원본 그라디언트가 전부 불투명이라
 *   `white` 로 눌러 중복 정의(`paint1`) 하나를 없앴다.
 *
 * 마크의 주황→핑크 그라디언트는 브랜드 고정이라 테마 토큰을 쓰지 않는다
 * (`Send your first POP!` 배경과 같은 취급).
 */
export function OnePopLogo({ className }: { className?: string }) {
  return (
    <svg
      width="116"
      height="24"
      viewBox="0 0 116 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      // 레터폼이 path 라 텍스트 노드가 없다 — 이름을 명시하지 않으면 헤더
      // 타이틀이 스크린리더에서 사라진다.
      role="img"
      aria-label="ONEpop"
      className={className}
    >
      <path
        d="M38.1949 4.06787C33.7184 4.06787 30.085 7.69479 30.085 12.1647C30.085 16.6346 33.7184 20.2616 38.1949 20.2616C42.6713 20.2616 46.3048 16.6346 46.3048 12.1647C46.3048 7.69479 42.6713 4.06787 38.1949 4.06787ZM38.1949 16.6216C35.7312 16.6216 33.7315 14.6284 33.7315 12.1647C33.7315 9.70103 35.7312 7.70786 38.1949 7.70786C40.6586 7.70786 42.6583 9.70103 42.6583 12.1647C42.6583 14.6284 40.6586 16.6216 38.1949 16.6216Z"
        fill="currentColor"
      />
      <path
        d="M52.0033 4.81283C51.6438 4.40113 51.1537 4.11359 50.5721 4.11359C49.5657 4.11359 48.7554 4.93046 48.7554 5.93031V18.4513C48.7554 19.4577 49.5722 20.2681 50.5721 20.2681C51.5719 20.2681 52.3888 19.4512 52.3888 18.4513V10.7727L60.5837 19.5492C60.9431 19.9609 61.4332 20.2485 62.0148 20.2485C63.0212 20.2485 63.8316 19.4316 63.8316 18.4317C63.8316 17.9351 63.6094 17.5038 63.3153 17.1639L51.9967 4.81936L52.0033 4.81283Z"
        fill="currentColor"
      />
      <path
        d="M61.8643 12.3934C62.9034 12.3934 63.7398 11.5504 63.7398 10.5179V5.96953C63.7398 4.93047 62.8968 4.09399 61.8643 4.09399C60.8318 4.09399 59.9888 4.93701 59.9888 5.96953V10.5179C59.9888 11.5569 60.8318 12.3934 61.8643 12.3934Z"
        fill="currentColor"
      />
      <path
        d="M76.9017 7.74704C77.9081 7.74704 78.7249 6.93016 78.7249 5.93031C78.7249 4.93046 77.9081 4.11359 76.9017 4.11359H68.5108C67.5044 4.11359 66.6875 4.93046 66.6875 5.93031V18.4513C66.6875 19.4577 67.5044 20.2681 68.5108 20.2681H76.9017C77.9081 20.2681 78.7249 19.4512 78.7249 18.4513C78.7249 17.4515 77.9081 16.6346 76.9017 16.6346H70.3275V14.0075H76.2286C77.235 14.0075 78.0518 13.1907 78.0518 12.1908C78.0518 11.191 77.235 10.3741 76.2286 10.3741H70.3275V7.74704H76.9017Z"
        fill="currentColor"
      />
      <path
        d="M84.5951 23.1908C84.3206 23.1908 84.105 23.1124 83.9481 22.9555C83.7913 22.7987 83.7129 22.5765 83.7129 22.2889V10.7416C83.7129 10.441 83.7913 10.2188 83.9481 10.0751C84.105 9.91823 84.3206 9.83981 84.5951 9.83981C84.8827 9.83981 85.0983 9.91823 85.2421 10.0751C85.3989 10.2188 85.4773 10.441 85.4773 10.7416V12.5257L85.2617 12.1728C85.4708 11.4539 85.8825 10.8789 86.4968 10.4476C87.1111 10.0163 87.8495 9.8006 88.7122 9.8006C89.5617 9.8006 90.3002 10.0032 90.9275 10.4084C91.5549 10.8004 92.045 11.369 92.3979 12.114C92.7508 12.8459 92.9272 13.7216 92.9272 14.741C92.9272 15.7474 92.7508 16.6297 92.3979 17.3877C92.0581 18.1327 91.5679 18.7078 90.9275 19.1129C90.3002 19.505 89.5617 19.7011 88.7122 19.7011C87.8626 19.7011 87.1307 19.4854 86.5164 19.0541C85.9021 18.6228 85.4904 18.0543 85.2813 17.3485H85.4969V22.2889C85.4969 22.5765 85.4185 22.7987 85.2617 22.9555C85.1048 23.1124 84.8827 23.1908 84.5951 23.1908ZM88.3004 18.2895C88.8625 18.2895 89.3526 18.1523 89.7708 17.8778C90.2021 17.6034 90.5354 17.2047 90.7707 16.6819C91.0059 16.1591 91.1236 15.5122 91.1236 14.741C91.1236 13.5909 90.8687 12.7152 90.359 12.114C89.8492 11.5128 89.1631 11.2122 88.3004 11.2122C87.7254 11.2122 87.2222 11.3494 86.7909 11.6239C86.3726 11.8853 86.0459 12.2774 85.8106 12.8002C85.5884 13.323 85.4773 13.9699 85.4773 14.741C85.4773 15.8912 85.7322 16.7734 86.2419 17.3877C86.7517 17.9889 87.4378 18.2895 88.3004 18.2895Z"
        fill="currentColor"
      />
      <path
        d="M99.2775 19.7011C98.3365 19.7011 97.5196 19.4985 96.8269 19.0933C96.1342 18.6882 95.5983 18.1131 95.2193 17.3681C94.8403 16.6231 94.6508 15.7474 94.6508 14.741C94.6508 13.983 94.7553 13.3033 94.9644 12.7021C95.1866 12.0878 95.5003 11.565 95.9055 11.1337C96.3106 10.7024 96.7942 10.3757 97.3562 10.1535C97.9313 9.91823 98.5718 9.8006 99.2775 9.8006C100.219 9.8006 101.035 10.0032 101.728 10.4084C102.421 10.8135 102.957 11.3886 103.336 12.1336C103.715 12.8655 103.904 13.7347 103.904 14.741C103.904 15.4991 103.793 16.1853 103.571 16.7996C103.362 17.4139 103.055 17.9366 102.65 18.368C102.244 18.7993 101.754 19.1326 101.179 19.3678C100.617 19.59 99.9833 19.7011 99.2775 19.7011ZM99.2775 18.2895C99.8526 18.2895 100.349 18.1523 100.768 17.8778C101.199 17.6034 101.526 17.2047 101.748 16.6819C101.983 16.1591 102.101 15.5122 102.101 14.741C102.101 13.5909 101.846 12.7152 101.336 12.114C100.826 11.5128 100.14 11.2122 99.2775 11.2122C98.7155 11.2122 98.2189 11.3494 97.7876 11.6239C97.3693 11.8853 97.0426 12.2774 96.8073 12.8002C96.572 13.323 96.4544 13.9699 96.4544 14.741C96.4544 15.8912 96.7093 16.7734 97.219 17.3877C97.7418 17.9889 98.428 18.2895 99.2775 18.2895Z"
        fill="currentColor"
      />
      <path
        d="M107.072 23.1908C106.797 23.1908 106.582 23.1124 106.425 22.9555C106.268 22.7987 106.19 22.5765 106.19 22.2889V10.7416C106.19 10.441 106.268 10.2188 106.425 10.0751C106.582 9.91823 106.797 9.83981 107.072 9.83981C107.359 9.83981 107.575 9.91823 107.719 10.0751C107.876 10.2188 107.954 10.441 107.954 10.7416V12.5257L107.738 12.1728C107.948 11.4539 108.359 10.8789 108.974 10.4476C109.588 10.0163 110.326 9.8006 111.189 9.8006C112.038 9.8006 112.777 10.0032 113.404 10.4084C114.032 10.8004 114.522 11.369 114.875 12.114C115.228 12.8459 115.404 13.7216 115.404 14.741C115.404 15.7474 115.228 16.6297 114.875 17.3877C114.535 18.1327 114.045 18.7078 113.404 19.1129C112.777 19.505 112.038 19.7011 111.189 19.7011C110.339 19.7011 109.607 19.4854 108.993 19.0541C108.379 18.6228 107.967 18.0543 107.758 17.3485H107.974V22.2889C107.974 22.5765 107.895 22.7987 107.738 22.9555C107.582 23.1124 107.359 23.1908 107.072 23.1908ZM110.777 18.2895C111.339 18.2895 111.829 18.1523 112.248 17.8778C112.679 17.6034 113.012 17.2047 113.247 16.6819C113.483 16.1591 113.6 15.5122 113.6 14.741C113.6 13.5909 113.345 12.7152 112.836 12.114C112.326 11.5128 111.64 11.2122 110.777 11.2122C110.202 11.2122 109.699 11.3494 109.268 11.6239C108.849 11.8853 108.523 12.2774 108.287 12.8002C108.065 13.323 107.954 13.9699 107.954 14.741C107.954 15.8912 108.209 16.7734 108.719 17.3877C109.228 17.9889 109.915 18.2895 110.777 18.2895Z"
        fill="currentColor"
      />
      <path
        d="M14.7326 0.727918C12.9726 1.59329 11.6096 4.00482 11.0833 5.59357C9.88384 4.95085 7.89924 3.76837 5.7791 4.00482C3.25593 4.28622 1.16093 5.72223 0.333058 8.59505C-0.616745 11.891 0.425001 15.7904 3.97287 17.5788C7.25977 19.2357 10.3262 21.1047 11.8468 22.2197C13.3673 23.3348 14.6332 24.4164 15.5435 23.8398C17.1655 23.054 20.2401 19.6295 22.4534 16.1013C24.3507 13.0769 25.9439 7.65453 23.3488 3.26211C21.2608 -0.0620856 17.6891 -0.725834 14.7326 0.727918Z"
        fill="url(#wi-op-logo-heart)"
      />
      <mask
        id="wi-op-logo-mask"
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="25"
        height="24"
      >
        <path
          d="M14.7326 0.727918C12.9726 1.59329 11.6096 4.00482 11.0833 5.59357C9.88384 4.95085 7.89924 3.76837 5.7791 4.00482C3.25593 4.28622 1.16093 5.72223 0.333058 8.59505C-0.616745 11.891 0.425001 15.7904 3.97287 17.5788C7.25977 19.2357 10.3262 21.1047 11.8468 22.2197C13.3673 23.3348 14.6332 24.4164 15.5435 23.8398C17.1655 23.054 20.2401 19.6295 22.4534 16.1013C24.3507 13.0769 25.9439 7.65453 23.3488 3.26211C21.2608 -0.0620856 17.6891 -0.725834 14.7326 0.727918Z"
          fill="white"
        />
      </mask>
      <g mask="url(#wi-op-logo-mask)">
        <path
          d="M29.5305 -3.85553H-1.43018V24.7597H29.5305V-3.85553Z"
          fill="url(#wi-op-logo-base)"
        />
        <path
          d="M12.7993 24.7597H29.5372V1.58752L12.7993 13.5165V24.7597Z"
          fill="url(#wi-op-logo-shine-r)"
        />
        <path
          d="M12.8037 24.7598H-1.63916V2.94476L12.8037 13.5166V24.7598Z"
          fill="url(#wi-op-logo-shine-l)"
        />
      </g>
      <path
        d="M13.4339 7.58409L9.79429 9.73601C9.1523 10.1136 8.92988 10.962 9.2989 11.6189C9.66791 12.2759 10.4969 12.5035 11.1389 12.1259L14.2174 10.305L12.7515 11.9138V18.2247C12.7515 18.9852 13.3581 19.6059 14.1012 19.6059C14.8442 19.6059 15.4508 18.9852 15.4508 18.2247V8.77903C15.4508 8.51521 15.37 8.25656 15.2689 8.08586C14.8897 7.43407 14.0708 7.20129 13.4288 7.57891L13.4339 7.58409Z"
        fill="white"
      />
      <defs>
        <linearGradient
          id="wi-op-logo-heart"
          x1="12.3679"
          y1="-0.18782"
          x2="12.3679"
          y2="23.9797"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F1603C" />
          <stop offset="1" stopColor="#EE2566" />
        </linearGradient>
        <linearGradient
          id="wi-op-logo-base"
          x1="-5.01624"
          y1="-7.30565"
          x2="28.6827"
          y2="29.4172"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F3752D" />
          <stop offset="1" stopColor="#EC1272" />
        </linearGradient>
        <linearGradient
          id="wi-op-logo-shine-r"
          x1="10.2312"
          y1="2.71613"
          x2="23.8708"
          y2="13.2539"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0.5" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id="wi-op-logo-shine-l"
          x1="24.1414"
          y1="-2.87687"
          x2="12.838"
          y2="18.379"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0.5" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/**
 * 히어로 일러스트 원본 데이터. Figma 내보내기(`Rectangle.svg`)는 이름만 SVG 고
 * 실제로는 1600×1410 PNG(735KB)를 감싼 래퍼였다. 이 패키지는 `dist` 만
 * 배포해서 에셋 파이프라인이 없으므로 인라인 말고는 방법이 없고, 735KB 를 그대로
 * base64 로 넣을 수는 없다. 실제 렌더 크기가 82×72 CSS px 이라 3x(246px)로 줄이고
 * WebP q90 으로 다시 압축했다 — 8KB(base64 10.6KB).
 */
const ONEPOP_HERO_ART_SRC =
  "data:image/webp;base64,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";

/**
 * 히어로 일러스트 — 하트를 받쳐 든 손. Figma 원본 래스터를 인라인한 것이며,
 * `OnePopBody` 의 `heroSrc` 로 다른 이미지를 주입해 덮어쓸 수 있다.
 *
 * 이전 재현본은 벡터라 `--wi-op-hero-line` 으로 아웃라인을 다크 테마에서 흰색으로
 * 뒤집었다. 래스터에는 그게 안 먹으므로 아웃라인은 어느 테마에서나 원본의 검정
 * 그대로다 — 흰 장갑 + 빨간 하트가 실루엣을 지고 있어 어두운 배경에서도 형태는
 * 읽힌다. 테마별로 갈라야 하면 이미지가 두 장 필요하다.
 *
 * `<img>` 가 아니라 `<svg><image>` 인 이유는 82×72 고정 크기를 CSS 없이 그 자체로
 * 갖게 하려는 것이다 — `.wi-op-hero-art svg` 를 잡는 소비자 CSS 도 그대로 산다.
 */
export function OnePopHeroArt({ className }: { className?: string }) {
  return (
    <svg
      width="82"
      height="72"
      viewBox="0 0 82 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={className}
    >
      <image
        href={ONEPOP_HERO_ART_SRC}
        width="82"
        height="72"
        preserveAspectRatio="xMidYMid meet"
      />
    </svg>
  );
}
